import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import type { ItemRow } from '~~/shared/items/itemRow'
import type { ItemRecord } from '~~/shared/items/itemRecord'
import type { FolderInfo } from '~~/shared/items/folderInfo'
import { isItemPending } from '~~/shared/items/itemPending'
import { fullPathOf as itemFullPath } from '~~/shared/items/itemPath'
import type { DocumentStatusFilter, DocumentTypeFilter } from '~~/shared/items/documentQuery'
import { createEntityCache, setWith, setWithout } from '~/stores/entityCache'
import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import { readPref, writePref } from '~/composables/usePersistedPref'
import type { FileExplorerSurface } from '~/composables/documents/fileExplorerSurface'
import {
  DEFAULT_SOURCES, SOURCES_KEY, orderSources, storedSources, type DocumentSource,
} from '~/composables/documents/sources'
import {
  SORT_FIELDS, type DeleteItemsResult, type ListingParams, type SortField, type SortOrder,
} from '~/composables/documents/browser'
import { useFolderListing } from '~/composables/documents/useFolderListing'
import { useOrgGroups } from '~/composables/documents/useOrgGroups'
import { useFolderTree } from '~/composables/documents/useFolderTree'
import { useDocumentSearch } from '~/composables/documents/useDocumentSearch'
import { rowFieldsOf, useDocumentDetail } from '~/composables/documents/useDocumentDetail'
import { useDocumentSelection } from '~/composables/documents/useDocumentSelection'

/**
 * THE documents store (MAR-125). One place a document exists, client-side.
 *
 * Replaces `blades/useBrowser.ts` (826 lines), `blades/useItems.ts` and
 * `composables/useDocumentBrowser.ts` — four independent copies of the same rows, of which only the
 * first was a singleton. The other three were plain `ref()`s, so every caller got its own list and
 * they drifted the moment anything changed. That is why the sidebar, the case page and `/app/docs`
 * could disagree about what exists.
 *
 * ## Cache + views, not one array
 *
 * The same documents arrive in different shapes and scopes: `/api/items` (flat listing for one path,
 * or `scope=org` for the whole library) and `/api/items/:id` (content + relations). Pour those into
 * one array and a case-scoped fetch silently pollutes the folder listing, because nothing records
 * which view a row arrived for.
 *
 * So the cache (`entityCache`) holds one copy per id, and every surface is an ordered **id list**
 * resolved through it. A row can appear in three views without existing three times, and removing it
 * once removes it everywhere.
 *
 * ## A facade (MAR-948)
 *
 * This file owns the cache, the preferences, the mutations and the wiring. Everything else is a
 * deps-injected module under `app/composables/documents/` — the folder listing, the org-wide buckets
 * (`useOrgGroups`), the folder tree, search, the detail overlay, selection and drag. The store id and
 * the return surface are unchanged, so no caller notices; `documentsStoreFacade.test.ts` pins both.
 *
 * ## Dependency direction
 *
 * documents → documentsIndexing, one way. That is what the rename encodes, and this file must stay
 * the only side that imports the other. Under `useBrowser` the two called into each other, and that
 * cycle is exactly why a newly-uploaded document never appeared until a reload (see `onSettled`).
 */

// Re-exported: callers import these from the store, as they always have.
export type { FolderInfo, DocumentSource, SortField, SortOrder, DeleteItemsResult }
export { DOCUMENT_SOURCES, orderSources } from '~/composables/documents/sources'

export const useDocumentsStore = defineStore('documents', () => {
  const { apiFetch } = useApi()

  /**
   * Resolved once here, at store setup — never inside a computed.
   *
   * A store factory called from a getter runs that store's setup during the computed's evaluation on
   * first access, and this store's setup ends in a `watch(jobs, …, { immediate: true })` that writes
   * to the activity store — i.e. a mutation inside a computed pass. Cheap to get right, miserable to
   * debug; it was a real bug (`23b4e434`).
   */
  const indexing = useDocumentsIndexingStore()

  const cache = createEntityCache<ItemRow>()

  /** Register any pending rows with the indexing store so the shared poller watches them. */
  function trackPending(rows: Array<{ id: string; name: string; file_name: string | null; status: string | null }>) {
    const pending = rows.filter(r => isItemPending(r.status))
    if (pending.length) indexing.track(pending, 'library')
    indexing.ensurePoll()
  }

  /**
   * ── THE FINDER'S TYPE/STATE LENS, AND WHY IT LIVES IN THE STORE (AlexAmin/marcus#169) ─────────
   *
   * It used to be two page-local `ref`s in `app/pages/app/docs/index.vue`, applied as an
   * `Array.filter` over the rows already loaded. Three things were wrong with that and only the
   * third is about tidiness:
   *
   * 1. It filtered a page and looked like it filtered the library, which is why the page's
   *    `Mostrando N de M` line had to be HIDDEN whenever either was active.
   * 2. In a grouped view it cut each band's loaded rows while the band header kept printing the
   *    server's unfiltered `total`.
   * 3. The rail renders the same components and could not see it.
   *
   * They are arrays, not one value each: a column-header menu is a checkbox list, and "PDF or DOCX"
   * is a question the single `<select>` they replace could not ask. An EMPTY array means "every one"
   * — the server builds the unfiltered query then, so nothing selected costs nothing.
   *
   * Not persisted, deliberately. A source selection is a way of working and survives the session; a
   * filter is a question you asked once, and a browser that reopens narrowed to Final with no
   * obvious reason is the same class of confusion as #93's pinned group mode.
   */
  const typeFilters = ref<DocumentTypeFilter[]>([])
  const statusFilters = ref<DocumentStatusFilter[]>([])
  const hasDocumentFilter = computed(() => typeFilters.value.length > 0 || statusFilters.value.length > 0)
  const sortField = ref<SortField>('name')
  const sortOrder = ref<SortOrder>('asc')
  /** The wire form, built in one place so the reads cannot drift on how they spell it. */
  const listingParams = computed<ListingParams>(() => ({
    sort: sortField.value,
    order: sortOrder.value,
    types: typeFilters.value.length ? typeFilters.value.join(',') : undefined,
    statuses: statusFilters.value.length ? statusFilters.value.join(',') : undefined,
  }))

  // ── The modules ───────────────────────────────────────────────────────────────────────────────
  const groups = useOrgGroups({ apiFetch, cache, listingParams, trackPending })
  const tree = useFolderTree({ apiFetch, cache, listingParams, trackPending })
  const listing = useFolderListing({
    apiFetch, cache, listingParams, trackPending, pendingUploadsIn: path => indexing.pendingUploadsIn(path),
    folders: tree.folders, foldersState: tree.foldersState,
  })
  const { currentPath, loading, loadingMore, items, folderRows, fetchItems } = listing
  const search = useDocumentSearch({
    apiFetch, cache, currentPath, typeFilters, statusFilters, loading, loadingMore, fetchItems: () => fetchItems(),
  })
  const detailOverlay = useDocumentDetail({ apiFetch, cache, trackPending })
  const selection = useDocumentSelection({
    cache, items, folderRows, expandedFolders: tree.expandedFolders, getChildren: tree.getChildren,
    moveItems: (ids, destination) => moveItems(ids, destination),
  })

  /**
   * The sidebar's grouped-mode filter text (MAR-494).
   *
   * Written by `FileExplorer` and read by `useWebChatView`, and by nobody else. It is here rather
   * than in the component because the agent has to be told what the user is looking at: `ViewState`
   * is assembled outside the component tree, so a filter that only exists as a local `ref` reports
   * as "no filter" however hard the user has narrowed the list.
   *
   * This is NOT the shared filter box MAR-125 removed. `/app/docs` binds its own box to
   * `searchQuery` and never touches this field, so typing in one surface still cannot filter the
   * other — which was the whole of that regression.
   */
  const groupFilter = ref('')
  /**
   * THE source filter (AlexAmin/marcus#158). Empty means no filter — the flat listing at
   * `currentPath` — and is the only way to express what used to be `groupMode === 'folders'`.
   *
   * Default is Clientes alone (#81): the first thing a lawyer does is organise the desk by client,
   * and the case is the folder inside it. It is never empty by default, because the folder tree left
   * the sidebar and lives on the full-page finder, which imposes the empty selection itself. A user
   * who has already chosen keeps their choice — `loadPreferences` overwrites this from localStorage.
   */
  const activeSources = ref<Set<DocumentSource>>(new Set(DEFAULT_SOURCES))
  /**
   * No source filter: the folder/flat listing. THE one predicate every surface branches on, and the
   * successor to `groupMode === 'folders'` — keep reading it through this rather than re-deriving
   * `size === 0` at call sites, so there is one definition of the distinction MAR-494 relies on.
   */
  const isFolderMode = computed(() => activeSources.value.size === 0)
  /** The selection in render order — what the picker's label and `useDocumentGroups` both read. */
  const orderedSources = computed(() => orderSources(activeSources.value))
  // Whether anything has set the selection this session. `loadPreferences` runs from the shell
  // layout, whose `onMounted` fires AFTER the page's — so on a hard load of /app/docs it would
  // otherwise undo the finder's own empty selection a tick later. Persisting the empty one used to
  // mask that ordering by accident, which is exactly the write #93 removes.
  const sourcesTouched = ref(false)

  // ── Cache writes ──────────────────────────────────────────────────────────────────────────────
  /**
   * Subscribers notified when documents leave the cache.
   *
   * This exists so the CASES store can mirror `case_documents`' `ON DELETE CASCADE` without this
   * store having to know cases exist. The dependency runs cases → documents (cases seeds document
   * rows on a case read); importing back the other way would be a cycle, and having each delete site
   * remember to call the cases store would be the same "remember to refresh" pattern that produced
   * the upload-staleness bug. Mirrors `documentsIndexing.onSettled`.
   */
  const removeListeners = new Set<(ids: string[]) => void>()

  function onRemoved(fn: (ids: string[]) => void): () => void {
    removeListeners.add(fn)
    return () => removeListeners.delete(fn)
  }

  /**
   * Drop rows from the cache and from EVERY view that referenced them — and only then tell the
   * listeners, so a listener re-deriving anything from this store never sees a removed row in some
   * view it has not been pruned from yet (`documentsStoreFacade.test.ts`).
   */
  function remove(ids: string[]) {
    if (ids.length === 0) return
    const gone = new Set(ids)
    cache.remove(ids)
    detailOverlay.prune(gone)
    listing.prune(gone)
    search.prune(gone)
    groups.prune(gone)
    tree.prune(gone)
    selection.prune(gone)
    for (const fn of removeListeners) fn(ids)
  }

  async function refreshAll() {
    await Promise.all([fetchItems(), tree.fetchFolders(), groups.fetchGroups()])
    await tree.refetchExpanded()
  }

  function setGroupFilter(query: string) {
    groupFilter.value = query
  }

  /**
   * THE FILE EXPLORERS ON SCREEN, as the chat sees them (MAR-494, MAR-989).
   *
   * A file explorer is any surface that lists the user's documents with a filter box: the sidebar
   * drawer (`FileExplorer`) and the full-page finder (`/app/docs`). Each one registers itself while
   * it is mounted, and that registration is the ONE answer to "is an explorer on screen" — for the
   * INTERFACE STATE line the agent reads (`useWebChatView`), for which rows that line lists, and for
   * whether `filter_files` / `select_files` reached anything. Before, the line followed
   * `docsDrawerOpen` while the tools followed this registration, and only the drawer registered —
   * so `/app/docs`, which closes the drawer because it IS the explorer, told the agent nothing was
   * on screen while the agent's filter visibly moved its list (CHAT-2.01, 2026-10-01).
   *
   * Each surface supplies its own rows and filter because each renders differently: the drawer
   * filters its groups client-side, the finder flattens a search across every mode. The surface
   * that renders a thing is the only one that can say what it renders (`shared/web-chat/viewState.ts`).
   *
   * The most recently mounted surface is the one the chat addresses. Two at once happens only when
   * the user reopens the sidebar over `/app/docs`; the drawer then mounted last and is the one the
   * user just reached for.
   */
  const fileExplorers = shallowRef<FileExplorerSurface[]>([])
  const fileExplorer = computed<FileExplorerSurface | null>(() => fileExplorers.value.at(-1) ?? null)

  /** Register a mounted explorer surface; returns the call that unregisters it on unmount. */
  function registerFileExplorer(surface: FileExplorerSurface): () => void {
    fileExplorers.value = [...fileExplorers.value, surface]
    return () => { fileExplorers.value = fileExplorers.value.filter(s => s !== surface) }
  }

  /**
   * Returns whether the filter reached a mounted explorer. `false` means nothing the user can see
   * moved — the store search still runs as a best-effort fallback, but the caller (the
   * `filter_files` chat tool) must not treat that as a success: reporting one when no explorer is
   * mounted is the fabricated-success bug in AlexAmin/marcus#795.
   */
  function filterFiles(query: string): boolean {
    const surface = fileExplorer.value
    if (surface) {
      surface.applyFilter(query)
      return true
    }
    void search.searchItems(query)
    return false
  }

  /**
   * How the `select_files` chat tool selects rows — and whether anyone can SEE it (MAR-991, after
   * MAR-989). The selection is set either way (it is shared state other surfaces read), but the
   * return says whether a file explorer is mounted to show it, by the same signal `filterFiles` uses:
   * reporting success with no explorer on screen is #795's fabricated success again.
   */
  function selectFiles(ids: string[]): boolean {
    selection.selectMany(ids)
    return fileExplorer.value !== null
  }

  // ── Upload settling ───────────────────────────────────────────────────────────────────────────
  /**
   * THE FIX for "the sidebar needs a page reload before new uploads appear" (MAR-125).
   *
   * `useBrowser` handled settling with a callback that asked, per surface, *"is this item already in
   * the list I am showing?"* and refetched only if so. A brand-new upload is by construction in none
   * of them, so no branch ever fired and the listing was never refetched. The rule here is
   * unconditional instead: a settled job means the library changed, so the library refetches.
   *
   * Subscribed once, at store setup: this store is a singleton, so this is the whole app's
   * subscription. There is deliberately no `ensureStatusPoll()` for callers to remember to call.
   */
  indexing.onSettled((job) => {
    // A job that settled without ever getting a server id was a refused upload — no row to refresh.
    if (!job.itemId) return
    void fetchItems()
    if (!isFolderMode.value) void groups.fetchGroups()
    // A document can be cached while living OUTSIDE the folder being browsed — chat uploads land in
    // the chat-uploads folder, and their attachment chips read status straight off the cached row.
    // The listing refetch above would never reach those, so refresh the row itself.
    if (detailOverlay.details.value.has(job.itemId)) void detailOverlay.refreshDetail(job.itemId)
    else if (cache.has(job.itemId)) void detailOverlay.refreshRow(job.itemId)
  })

  // ── Mutations ─────────────────────────────────────────────────────────────────────────────────
  // `POST /api/items` and `PUT /api/items/:id` both return the bare `items` row — an `ItemRecord`,
  // not an `ItemRow`, and must not be filed as one: only `rowFieldsOf` is merged into the cache.
  /**
   * The folder `name` under `path`, created if missing. Idempotent server-side (`createItem` returns
   * an existing same-named folder as itself), and refreshes nothing — for a caller about to write into
   * it, like the chat's uploads folder, whose own refresh follows.
   */
  async function ensureFolder(name: string, path: string): Promise<ItemRecord> {
    return apiFetch<ItemRecord>('/api/items', { method: 'POST', body: { type: 'folder', name, path } })
  }

  async function createFolder(name: string): Promise<ItemRecord> {
    const item = await ensureFolder(name, currentPath.value)
    await refreshAll()
    return item
  }

  async function renameItem(id: string, name: string): Promise<ItemRecord> {
    const item = await apiFetch<ItemRecord>(`/api/items/${id}`, { method: 'PUT', body: { name } })
    cache.patch(id, { ...rowFieldsOf(item), slug: item.slug })
    await refreshAll()
    return item
  }

  async function moveItems(ids: string[], destination: string) {
    // Optimistically prune the moved rows; refreshAll reconciles with the server.
    remove(ids)
    try {
      await apiFetch('/api/items/move', { method: 'POST', body: { ids, destination } })
    } finally {
      await refreshAll()
    }
  }

  async function deleteItems(ids: string[]): Promise<DeleteItemsResult> {
    const uniqueIds = [...new Set(ids)].filter(Boolean)
    const result: DeleteItemsResult = { deletedIds: [], failedIds: [] }
    if (uniqueIds.length === 0) return result

    for (const id of uniqueIds) {
      try {
        await apiFetch(`/api/items/${id}`, { method: 'DELETE' })
        result.deletedIds.push(id)
      } catch (err) {
        result.failedIds.push(id)
        console.error('[marcus:documents] Failed to delete item:', err)
      }
    }

    if (result.deletedIds.length > 0) remove(result.deletedIds)
    await refreshAll()

    if (result.failedIds.length > 0) {
      const error = new Error(
        result.failedIds.length === 1
          ? 'Could not delete the selected item.'
          : `Could not delete ${result.failedIds.length} selected items.`,
      )
      ;(error as Error & { result?: DeleteItemsResult }).result = result
      throw error
    }
    return result
  }

  async function deleteItem(id: string) {
    await deleteItems([id])
  }

  async function deleteSelected(): Promise<DeleteItemsResult> {
    return await deleteItems([...selection.selectedIds.value])
  }

  // ── Navigation ────────────────────────────────────────────────────────────────────────────────
  function navigateTo(path: string) {
    currentPath.value = path
    selection.clearSelection()
    search.resetSearch()
    void fetchItems()
    writePref('marcus:browser-path', path)
  }

  function openFolder(item: ItemRow) {
    navigateTo(itemFullPath(item))
  }

  // ── Preferences ───────────────────────────────────────────────────────────────────────────────
  function setSort(field: SortField, order?: SortOrder) {
    if (order) {
      sortField.value = field
      sortOrder.value = order
    } else if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    writePref('marcus:browser-sort', sortField.value)
    writePref('marcus:browser-order', sortOrder.value)
    // BOTH modes refetch (AlexAmin/marcus#169). Grouped mode used to re-sort each band's loaded page
    // in the renderer — ordering a truncated prefix and presenting it as the band's order, a lie
    // under a column header with an arrow on it. `listOrgDocuments` now orders both its per-group
    // windows and its final select, so the prefix and its order agree.
    if (isFolderMode.value) void fetchItems()
    else void groups.fetchGroups()
  }

  /**
   * Replace the finder's type/state lens and refetch whichever read is on screen
   * (AlexAmin/marcus#169).
   *
   * ONE entry point, and it always refetches. The filters are server-side now, so a caller that set
   * the refs directly would narrow nothing while marking a column header as active. `refreshAll` is
   * not used: it also refetches the folder tree, which no filter can change. Both reads are
   * refreshed rather than only the active one, because the finder and the rail can be on screen
   * together and read different lenses of the same store.
   */
  function setDocumentFilters(next: { types?: DocumentTypeFilter[]; statuses?: DocumentStatusFilter[] }) {
    if (next.types) typeFilters.value = [...next.types]
    if (next.statuses) statusFilters.value = [...next.statuses]
    selection.clearSelection()
    if (search.searchQuery.value.trim()) void search.searchItems(search.searchQuery.value)
    else void fetchItems()
    void groups.fetchGroups()
    // Expanded folders show the filtered population too, so a new lens refetches their children
    // rather than leaving the previous lens's rows under them (MAR-957).
    void tree.refetchExpanded()
  }

  /** Drop the lens entirely — the "Limpiar" affordance, and what the chat file filter calls. */
  function clearDocumentFilters() {
    if (!hasDocumentFilter.value) return
    setDocumentFilters({ types: [], statuses: [] })
  }

  /**
   * Replace the whole source selection.
   *
   * `persist: false` is for a selection the USER did not choose — the finder imposing the empty one
   * while it is open, and handing it back when it closes. Those writes used to persist, so simply
   * visiting /app/docs pinned the sidebar into Casos mode for every later page view, where the sheet
   * and conversation groups do not render (AlexAmin/marcus#93). A selection the user makes in the
   * picker still persists; one a page imposes on its way past must not.
   */
  function setSources(next: Iterable<DocumentSource>, options: { persist?: boolean } = {}) {
    const ordered = orderSources(next)
    activeSources.value = new Set(ordered)
    sourcesTouched.value = true
    groupFilter.value = ''
    // Changing the grouping clears the column filters, for the same reason it already clears the
    // group filter box: the new selection is a DIFFERENT POPULATION, and a lens carried across it
    // narrows a list the user has not seen yet.
    typeFilters.value = []
    statusFilters.value = []
    selection.clearSelection()
    if (options.persist !== false) writePref(SOURCES_KEY, JSON.stringify(ordered))
    if (ordered.length === 0) {
      groups.resetGroups()
      void fetchItems()
      // The folder listing reads the conversation/sheet counts for its signposts, so the reset above
      // has to be followed by a refill rather than left at zero (AlexAmin/marcus#145).
    }
    void groups.fetchGroups()
  }

  /**
   * Add or remove ONE source — what a checkbox in the picker calls. Always a user action, so it
   * always persists; a page imposing a selection goes through `setSources` instead.
   */
  function toggleSource(source: DocumentSource) {
    const held = activeSources.value
    setSources(held.has(source) ? setWithout(held, source) : setWith(held, source))
  }

  /**
   * Put the selection back to what the user last chose, WITHOUT writing anything — what a page that
   * imposed its own calls on the way out (AlexAmin/marcus#93).
   */
  function restorePersistedSources() {
    setSources(storedSources() ?? DEFAULT_SOURCES, { persist: false })
  }

  function loadPreferences() {
    if (!import.meta.client) return
    const savedPath = readPref('marcus:browser-path')
    if (savedPath) currentPath.value = savedPath
    // Only while nothing has set the selection yet: a page mounted before this layout runs (the
    // finder, which imposes the empty one) has already made a deliberate choice, and this would
    // silently undo it.
    const saved = storedSources()
    if (saved && !sourcesTouched.value) activeSources.value = new Set(saved)
    const savedSort = readPref('marcus:browser-sort')
    if (savedSort && SORT_FIELDS.includes(savedSort as SortField)) sortField.value = savedSort as SortField
    const savedOrder = readPref('marcus:browser-order')
    if (savedOrder === 'asc' || savedOrder === 'desc') sortOrder.value = savedOrder
  }

  return {
    // cache
    byId: cache.byId, get: cache.get, upsert: cache.upsert, remove,
    detail: detailOverlay.detail, details: detailOverlay.details,
    // views
    items, folderRows, searchResults: search.searchResults, getChildren: tree.getChildren,
    folders: tree.folders, breadcrumb: listing.breadcrumb,
    groupedIds: groups.groupedIds, groupedDocuments: groups.groupedDocuments,
    ungroupedIds: groups.ungroupedIds, ungroupedDocuments: groups.ungroupedDocuments, ungroupedTotal: groups.ungroupedTotal,
    conversationLinkedIds: groups.conversationLinkedIds, conversationTotal: groups.conversationTotal,
    sheetDocumentIds: groups.sheetDocumentIds, sheetGroupsMeta: groups.sheetGroupsMeta,
    sheetLinkedIds: groups.sheetLinkedIds, sheetTotal: groups.sheetTotal,
    conversationDocumentIds: groups.conversationDocumentIds, conversationGroupsMeta: groups.conversationGroupsMeta,
    unattachedIds: groups.unattachedIds, unattachedTotal: groups.unattachedTotal,
    clientDirectIds: groups.clientDirectIds, clientDirectTotals: groups.clientDirectTotals,
    caseFilteredTotals: groups.caseFilteredTotals, groupedRank: groups.groupedRank,
    markUngrouped: groups.markUngrouped, unmarkUngrouped: groups.unmarkUngrouped, markUnattached: groups.markUnattached,
    linkToClient: groups.linkToClient, unlinkFromClient: groups.unlinkFromClient,
    onRemoved,
    flatVisibleItems: selection.flatVisibleItems, flatVisibleIds: selection.flatVisibleIds,
    setNavigationDomain: selection.setNavigationDomain,
    // state
    currentPath, loading, loadingMore, groupsLoading: groups.groupsLoading, searchQuery: search.searchQuery,
    groupFilter, sortField, sortOrder, activeSources, isFolderMode, orderedSources,
    folderTotal: listing.folderTotal, hasMoreItems: listing.hasMoreItems, searchTotal: search.searchTotal, hasMoreSearchResults: search.hasMoreSearchResults,
    searchState: search.searchState, searchFailed: search.searchFailed,
    // Whether each surface's last request ANSWERED (AlexAmin/marcus#469). Read by the finder, the
    // sidebar, `BrowserGroupedList` and `BrowserItemList` to displace an empty sentence they would
    // otherwise state over a failure.
    listState: listing.listState, listFailed: listing.listFailed, foldersState: tree.foldersState, foldersFailed: tree.foldersFailed,
    groupsState: groups.groupsState, groupsFailed: groups.groupsFailed, isChildrenFailed: tree.isChildrenFailed,
    selectedIds: selection.selectedIds, selectionAnchorId: selection.selectionAnchorId,
    expandedFolders: tree.expandedFolders, dragItemId: selection.dragItemId, dropTargetId: selection.dropTargetId,
    // fetching
    fetchItems, loadMoreItems: listing.loadMoreItems, loadMoreSearchResults: search.loadMoreSearchResults, fetchFolders: tree.fetchFolders,
    fetchGroups: groups.fetchGroups, fetchChildren: tree.fetchChildren, bootstrap: listing.bootstrap, searchItems: search.searchItems,
    refreshAll, setGroupFilter, registerFileExplorer, fileExplorer, filterFiles, selectFiles,
    // detail — `refreshRow` is exported alongside `refreshDetail` so the "safe to ignore" verdict on
    // its catch is assertable rather than only claimed in a comment (AlexAmin/marcus#469). It has no
    // component caller and is not meant to grow one.
    loadDetail: detailOverlay.loadDetail, refreshRow: detailOverlay.refreshRow,
    refreshDetail: detailOverlay.refreshDetail, forgetDetail: detailOverlay.forgetDetail,
    updateDetail: detailOverlay.updateDetail,
    // mutations
    createFolder, ensureFolder, renameItem, moveItems, deleteItem, deleteItems, deleteSelected,
    // navigation + tree
    navigateTo, openFolder, itemFullPath, expandFolder: tree.expandFolder, collapseFolder: tree.collapseFolder,
    toggleExpand: tree.toggleExpand, isExpanded: tree.isExpanded, isChildrenLoading: tree.isChildrenLoading,
    // selection
    selectOnly: selection.selectOnly, toggleSelected: selection.toggleSelected, selectRange: selection.selectRange,
    selectMany: selection.selectMany, clearSelection: selection.clearSelection,
    moveSelection: selection.moveSelection, focusedItem: selection.focusedItem,
    // drag + drop
    startDrag: selection.startDrag, endDrag: selection.endDrag, dropOnto: selection.dropOnto,
    isValidFolderDrop: selection.isValidFolderDrop,
    // preferences
    setSort, setSources, toggleSource, restorePersistedSources, loadPreferences,
    // the finder's type/state lens (AlexAmin/marcus#169) — server-side, one setter
    typeFilters, statusFilters, hasDocumentFilter, setDocumentFilters, clearDocumentFilters,
  }
})
